/**
 * Portal Sinau - Central Permission System & Role Matrix
 * Single source of truth for all role-based authorization rules.
 */

export const ROLES = {
  ADMIN: 'admin',
  DOSEN: 'dosen',
  MAHASISWA: 'mahasiswa'
}

export const PERMISSIONS = {
  // Common Features (Accessible to All Authenticated Users)
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_PROFILE: 'view_profile',
  VIEW_ONLINE_USERS: 'view_online_users',
  CHANGE_PASSWORD: 'change_password',
  VIEW_NOTIFICATIONS: 'view_notifications',
  VIEW_STUDENT_DIRECTORY: 'view_student_directory',
  VIEW_LECTURER_DIRECTORY: 'view_lecturer_directory',

  // Academic Courses
  VIEW_MY_COURSES: 'view_my_courses',
  VIEW_ALL_COURSES: 'view_all_courses',
  VIEW_COURSE_CATALOG: 'view_course_catalog',
  MANAGE_COURSES: 'manage_courses',

  // Tasks & Assignments
  VIEW_MY_TASKS: 'view_my_tasks',
  VIEW_ALL_TASKS: 'view_all_tasks',
  MANAGE_TASKS: 'manage_tasks',
  SUBMIT_TASKS: 'submit_tasks',

  // Grades & Transcripts
  VIEW_MY_GRADES: 'view_my_grades',
  VIEW_CLASS_GRADES: 'view_class_grades',
  VIEW_ALL_GRADES: 'view_all_grades',
  MANAGE_GRADES: 'manage_grades',

  // Administrative Management (Admin Only)
  MANAGE_USERS: 'manage_users',
  MANAGE_ANNOUNCEMENTS: 'manage_announcements',
  MANAGE_QUOTES: 'manage_quotes',
  MANAGE_SETTINGS: 'manage_settings',
  BULK_IMPORT: 'bulk_import',
  SYSTEM_MIGRATION: 'system_migration',
  EDIT_LANDING_PAGE: 'edit_landing_page'
}

/**
 * Normalize role string to canonical value ('admin', 'dosen', 'mahasiswa')
 */
export function normalizeRole(role) {
  if (!role) return ROLES.MAHASISWA
  const lower = String(role).trim().toLowerCase()
  if (lower === 'admin' || lower === 'administrator') return ROLES.ADMIN
  if (lower === 'dosen' || lower === 'lecturer' || lower === 'pengajar') return ROLES.DOSEN
  if (lower === 'mahasiswa' || lower === 'student') return ROLES.MAHASISWA
  return ROLES.MAHASISWA
}

/**
 * Get canonical home route for a role
 */
export function getRoleHome(role) {
  const normRole = normalizeRole(role)
  return `/${normRole}/dashboard`
}

/**
 * Role Permissions Matrix
 */
export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: new Set([
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_PROFILE,
    PERMISSIONS.VIEW_ONLINE_USERS,
    PERMISSIONS.CHANGE_PASSWORD,
    PERMISSIONS.VIEW_NOTIFICATIONS,
    PERMISSIONS.VIEW_STUDENT_DIRECTORY,
    PERMISSIONS.VIEW_LECTURER_DIRECTORY,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_MY_COURSES,
    PERMISSIONS.VIEW_ALL_COURSES,
    PERMISSIONS.VIEW_COURSE_CATALOG,
    PERMISSIONS.MANAGE_COURSES,
    PERMISSIONS.VIEW_MY_TASKS,
    PERMISSIONS.VIEW_ALL_TASKS,
    PERMISSIONS.MANAGE_TASKS,
    PERMISSIONS.SUBMIT_TASKS,
    PERMISSIONS.VIEW_MY_GRADES,
    PERMISSIONS.VIEW_CLASS_GRADES,
    PERMISSIONS.VIEW_ALL_GRADES,
    PERMISSIONS.MANAGE_GRADES,
    PERMISSIONS.MANAGE_ANNOUNCEMENTS,
    PERMISSIONS.MANAGE_QUOTES,
    PERMISSIONS.MANAGE_SETTINGS,
    PERMISSIONS.BULK_IMPORT,
    PERMISSIONS.SYSTEM_MIGRATION,
    PERMISSIONS.EDIT_LANDING_PAGE
  ]),

  [ROLES.DOSEN]: new Set([
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_PROFILE,
    PERMISSIONS.VIEW_ONLINE_USERS,
    PERMISSIONS.CHANGE_PASSWORD,
    PERMISSIONS.VIEW_NOTIFICATIONS,
    PERMISSIONS.VIEW_STUDENT_DIRECTORY,
    PERMISSIONS.VIEW_LECTURER_DIRECTORY,
    PERMISSIONS.VIEW_MY_COURSES,
    PERMISSIONS.VIEW_COURSE_CATALOG,
    PERMISSIONS.MANAGE_COURSES,
    PERMISSIONS.VIEW_MY_TASKS,
    PERMISSIONS.MANAGE_TASKS,
    PERMISSIONS.VIEW_MY_GRADES,
    PERMISSIONS.VIEW_CLASS_GRADES,
    PERMISSIONS.MANAGE_GRADES
  ]),

  [ROLES.MAHASISWA]: new Set([
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_PROFILE,
    PERMISSIONS.VIEW_ONLINE_USERS,
    PERMISSIONS.CHANGE_PASSWORD,
    PERMISSIONS.VIEW_NOTIFICATIONS,
    PERMISSIONS.VIEW_STUDENT_DIRECTORY,
    PERMISSIONS.VIEW_LECTURER_DIRECTORY,
    PERMISSIONS.VIEW_MY_COURSES,
    PERMISSIONS.VIEW_COURSE_CATALOG,
    PERMISSIONS.VIEW_MY_TASKS,
    PERMISSIONS.SUBMIT_TASKS,
    PERMISSIONS.VIEW_MY_GRADES
  ])
}

/**
 * Check if a role possesses a specific permission
 */
export function hasPermission(role, permission) {
  const normRole = normalizeRole(role)
  const permissions = ROLE_PERMISSIONS[normRole]
  return permissions ? permissions.has(permission) : false
}

/**
 * Map viewKey / route to permission
 */
export const VIEW_PERMISSION_MAP = {
  dashboard: PERMISSIONS.VIEW_DASHBOARD,
  courses: PERMISSIONS.VIEW_MY_COURSES,
  'all-courses': PERMISSIONS.VIEW_ALL_COURSES,
  catalog: PERMISSIONS.VIEW_COURSE_CATALOG,
  'data-mahasiswa': PERMISSIONS.VIEW_STUDENT_DIRECTORY,
  lecturers: PERMISSIONS.VIEW_LECTURER_DIRECTORY,
  'data-dosen': PERMISSIONS.VIEW_LECTURER_DIRECTORY,
  assignments: [PERMISSIONS.VIEW_MY_TASKS, PERMISSIONS.MANAGE_TASKS],
  'all-assignments': PERMISSIONS.VIEW_ALL_TASKS,
  grades: [PERMISSIONS.VIEW_MY_GRADES, PERMISSIONS.VIEW_CLASS_GRADES, PERMISSIONS.MANAGE_GRADES],
  'all-grades': PERMISSIONS.VIEW_ALL_GRADES,
  users: PERMISSIONS.MANAGE_USERS,
  'online-users': PERMISSIONS.VIEW_ONLINE_USERS,
  'change-password': PERMISSIONS.CHANGE_PASSWORD,
  announcements: PERMISSIONS.MANAGE_ANNOUNCEMENTS,
  quotes: PERMISSIONS.MANAGE_QUOTES,
  settings: PERMISSIONS.MANAGE_SETTINGS,
  'bulk-import': PERMISSIONS.BULK_IMPORT,
  'system-migration': PERMISSIONS.SYSTEM_MIGRATION,
  'landing-editor': PERMISSIONS.EDIT_LANDING_PAGE,
  profil: PERMISSIONS.VIEW_PROFILE
}

/**
 * Check if a role can access a specific view / route
 */
export function canAccessView(role, viewKey) {
  const normRole = normalizeRole(role)

  // Strict Admin-only features
  const adminOnlyViews = ['users', 'all-courses', 'all-assignments', 'all-grades', 'bulk-import', 'system-migration', 'landing-editor', 'settings', 'quotes']
  if (adminOnlyViews.includes(viewKey)) {
    return normRole === ROLES.ADMIN
  }

  const requiredPermission = VIEW_PERMISSION_MAP[viewKey]
  if (!requiredPermission) return true

  if (Array.isArray(requiredPermission)) {
    return requiredPermission.some(perm => hasPermission(normRole, perm))
  }
  return hasPermission(normRole, requiredPermission)
}
